'use client'

import { useEffect, useState } from 'react'
import { REFERRAL_TERMS } from '@/lib/referral/referral-terms'

interface Props {
  /** The referral code from the ?ref= URL param or stored value. */
  code: string
}

/**
 * Referral invite banner shown on /signup?ref=CODE and /pricing when a
 * referral code is present. Fetches the referrer's first name from
 * /api/referral/lookup and shows personalised copy:
 *
 *   "[Name] invited you to UnlimitedTests.
 *    Subscribe to Pro and get a month free — …" (REFERRAL_TERMS.friendHeadline)
 *
 * Falls back to generic copy if the name can't be resolved.
 */
export function ReferralBanner({ code }: Props) {
  const [referrerName, setReferrerName] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!code) return
    fetch(`/api/referral/lookup?code=${encodeURIComponent(code)}`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((data) => {
        if (typeof data?.name === 'string') setReferrerName(data.name)
      })
      .catch(() => {
        // Ignore fetch errors — fall back to generic copy.
      })
      .finally(() => setLoaded(true))
  }, [code])

  if (!code) return null
  // Don't flash generic copy then swap to personalised — wait until loaded.
  if (!loaded) return null

  const headline = referrerName
    ? `${referrerName} invited you to UnlimitedTests.`
    : 'You were invited to UnlimitedTests.'

  return (
    <div className="mb-5 rounded-lg border border-brand/30 bg-brand/10 px-4 py-3 text-sm">
      <p className="font-semibold text-brand-ink">{headline}</p>
      <p className="mt-0.5 text-brand-ink">{REFERRAL_TERMS.friendHeadline}</p>
    </div>
  )
}
